import React, { useEffect, useRef } from 'react';
import logoUrl from './Images/logo.jpg';
import { longDate, shortDate } from './dates';

/**
 * The two "default videos" that fill the gaps beside the logo when a day has no advertisement pictures:
 * left – about Royal Journalism (the contents of the About button); right – today's paper, every page, with the logo in its left corner.
 * They are drawn live on a canvas, so there is no video file to host, and they follow the date and the number of pages by themselves.
 */

type Draw = (ctx: CanvasRenderingContext2D, w: number, h: number, t: number) => void;

const still = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const ease = (x: number) => 1 - Math.pow(1 - Math.min(1, Math.max(0, x)), 3);
const FONT = '"Segoe UI", "Nirmala UI", system-ui, -apple-system, Roboto, sans-serif';

/** A canvas that fills its box (sharp on high-density screens) and calls draw() about 30 times a second while it is on screen. */
function useCanvas(draw: Draw) {
  const ref = useRef<HTMLCanvasElement>(null);
  const fn = useRef(draw);
  fn.current = draw;
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    let w = 0, h = 0, dpr = 1, raf = 0, last = 0;
    const calm = still();
    const size = () => {
      dpr = Math.min(2, window.devicePixelRatio || 1);
      w = cv.clientWidth;
      h = cv.clientHeight;
      cv.width = Math.max(1, Math.round(w * dpr));
      cv.height = Math.max(1, Math.round(h * dpr));
    };
    size();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(size) : null;
    ro?.observe(cv);
    const t0 = performance.now();
    const tick = (now: number) => {
      if (now - last >= 33) {
        last = now;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        if (w > 0 && h > 0) {
          try {
            fn.current(ctx, w, h, calm ? 1 : Math.max(0, now - t0) / 1000);
          } catch {
            /* a bad frame must never stop the animation or the page */
          }
        }
      }
      if (!calm) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, []);
  return ref;
}

function fit(ctx: CanvasRenderingContext2D, text: string, weight: string, size: number, maxW: number, min: number) {
  let s = size;
  ctx.font = `${weight} ${s}px ${FONT}`;
  while (s > min && ctx.measureText(text).width > maxW) {
    s -= 1;
    ctx.font = `${weight} ${s}px ${FONT}`;
  }
  return s;
}

/** Breaks text into lines that fit maxW at the biggest size (from max down to min) that keeps it within maxLines. */
function wrap(ctx: CanvasRenderingContext2D, text: string, weight: string, max: number, min: number, maxW: number, maxLines: number) {
  for (let size = max; size >= min; size--) {
    ctx.font = `${weight} ${size}px ${FONT}`;
    const lines: string[] = [];
    let cur = '';
    let ok = true;
    for (const word of text.split(' ')) {
      const t = cur ? cur + ' ' + word : word;
      if (ctx.measureText(t).width <= maxW) cur = t;
      else {
        if (!cur || ctx.measureText(word).width > maxW) { ok = false; break; }
        lines.push(cur);
        cur = word;
      }
    }
    if (ok) {
      lines.push(cur);
      if (lines.length <= maxLines) return { size, lines };
    }
  }
  ctx.font = `${weight} ${min}px ${FONT}`;
  return { size: min, lines: [text] };
}

function rounded(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/* ---------------------------------------------------------------- about */

const SCENES: { kicker: string; main: string; sub?: string }[] = [
  { kicker: 'About us', main: 'Royal Journalism', sub: 'నిజానికి రాయల్ స్వరం  •  ద్విభాషా దినపత్రిక' },
  { kicker: 'Chief-Editor', main: 'Challa Srinivasa Rao', sub: 'Phone 7396014218' },
  { kicker: 'Editor', main: 'Challa Srikanth' },
  { kicker: 'Advertise with us', main: '7396014218', sub: 'Call the Chief-Editor' },
  { kicker: 'Developed by', main: 'Jaya kumar Garikipati', sub: '+91 9966696911  •  +91 8148118148' },
];
const SCENE_SECONDS = 3.4;

export function AboutVideo() {
  const ref = useCanvas((ctx, w, h, t) => {
    const g = ctx.createLinearGradient(0, 0, w, 0);
    g.addColorStop(0, '#0f172a');
    g.addColorStop(1, '#7f1d1d');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    // a slow sheen sweeping across
    const sx = ((t * 70) % (w + 240)) - 120;
    const sg = ctx.createLinearGradient(sx - 60, 0, sx + 60, 0);
    sg.addColorStop(0, 'rgba(255,255,255,0)');
    sg.addColorStop(0.5, 'rgba(255,255,255,.09)');
    sg.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sg;
    ctx.fillRect(0, 0, w, h);

    const i = Math.floor(t / SCENE_SECONDS) % SCENES.length;
    const p = (t % SCENE_SECONDS) / SCENE_SECONDS;
    const s = SCENES[i];
    const a = Math.min(ease(p / 0.14), ease((1 - p) / 0.1));
    const dy = (1 - ease(p / 0.16)) * h * 0.12;
    ctx.save();
    ctx.globalAlpha = a;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const maxW = w * 0.9;
    if (w < 220) {
      // a small box (phone): the heading and the name, the name wrapped over up to three lines
      const kS2 = Math.max(7, Math.min(10, h * 0.14));
      const m = wrap(ctx, s.main, '800', Math.max(10, h * 0.24), 8, maxW, 3);
      const lh = m.size * 1.12;
      const tot = kS2 + 4 + lh * m.lines.length;
      let yy = (h - tot) / 2 + dy;
      ctx.fillStyle = '#fbbf24';
      ctx.font = `800 ${kS2}px ${FONT}`;
      ctx.fillText(s.kicker.toUpperCase(), w / 2, yy + kS2 / 2);
      yy += kS2 + 4;
      ctx.fillStyle = '#fff';
      ctx.font = `800 ${m.size}px ${FONT}`;
      m.lines.forEach((ln, k) => ctx.fillText(ln, w / 2, yy + lh * k + lh / 2));
      ctx.restore();
      return;
    }
    const kS = Math.max(9, h * 0.15), mS = Math.max(14, h * 0.3), sS = Math.max(10, h * 0.16);
    const rows = s.sub ? 3 : 2;
    const total = kS + mS + (s.sub ? sS : 0) + (rows - 1) * h * 0.05;
    let y = (h - total) / 2 + dy;
    ctx.fillStyle = '#fbbf24';
    fit(ctx, s.kicker.toUpperCase(), '800', kS, maxW, 8);
    ctx.fillText(s.kicker.toUpperCase(), w / 2, y + kS / 2);
    y += kS + h * 0.05;
    ctx.fillStyle = '#ffffff';
    fit(ctx, s.main, '800', mS, maxW, 12);
    ctx.fillText(s.main, w / 2, y + mS / 2);
    y += mS + h * 0.05;
    if (s.sub) {
      ctx.fillStyle = '#e5e7eb';
      fit(ctx, s.sub, '600', sS, maxW, 9);
      ctx.fillText(s.sub, w / 2, y + sS / 2);
    }
    ctx.restore();
    // progress dots
    const r = Math.max(2, h * 0.025);
    for (let k = 0; k < SCENES.length; k++) {
      ctx.beginPath();
      ctx.arc(w / 2 + (k - (SCENES.length - 1) / 2) * r * 4, h - r * 2.2, r, 0, Math.PI * 2);
      ctx.fillStyle = k === i ? '#fbbf24' : 'rgba(255,255,255,.35)';
      ctx.fill();
    }
  });
  return <canvas ref={ref} className="lb-video" role="img" aria-label="About Royal Journalism: editors, advertising phone number and developer" />;
}

/* ---------------------------------------------------------------- today's paper */

export function PaperVideo({ date, urls }: { date: string; urls: string[] }) {
  const logo = useRef<HTMLImageElement | null>(null);
  const pages = useRef<HTMLImageElement[]>([]);
  useEffect(() => {
    const l = new Image();
    l.src = logoUrl;
    logo.current = l;
  }, []);
  useEffect(() => {
    pages.current = urls.slice(0, 40).map((u) => {
      const im = new Image();
      im.src = u;
      return im;
    });
  }, [urls]);
  const n = Math.min(urls.length, 40);
  const label = (() => {
    try { return longDate(date); } catch { return date; }
  })();

  const ref = useCanvas((ctx, w, h, t) => {
    const g = ctx.createLinearGradient(0, 0, w, 0);
    g.addColorStop(0, '#111827');
    g.addColorStop(1, '#1f2937');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    // left corner: the logo, then the date and the page count
    const narrow = w < 220;
    const lw = narrow ? Math.min(w * 0.5, 54) - 8 : Math.min(h * 0.4, 46) * (1248 / 374);
    const lh = lw / (1248 / 374);
    const pw = lw + (narrow ? 10 : 18);
    const lg = logo.current;
    if (lg && lg.complete && lg.naturalWidth) {
      ctx.save();
      rounded(ctx, 8, 8, lw, lh, 4);
      ctx.clip();
      ctx.drawImage(lg, 8, 8, lw, lh);
      ctx.restore();
    }
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    let y = 8 + lh + h * 0.06;
    if (h >= 64 && !narrow) {
      const s1 = Math.max(8, h * 0.12);
      ctx.fillStyle = '#fbbf24';
      fit(ctx, "TODAY'S PAPER", '800', s1, pw - 12, 7);
      ctx.fillText("TODAY'S PAPER", 8, y);
      y += s1 + 3;
    }
    const s2 = narrow ? 9 : Math.max(9, h * 0.14);
    ctx.fillStyle = '#fff';
    let dl = narrow ? shortDate(date).replace(/,? \d{4}$/, '') : label;
    if (fit(ctx, dl, '700', s2, pw - 10, 9) <= 9 && ctx.measureText(dl).width > pw - 10) {
      try { dl = shortDate(date); } catch { /* keep the long form */ }
      fit(ctx, dl, '700', s2, pw - 10, 8);
    }
    ctx.fillText(dl, 8, y);
    y += s2 + 3;
    if (h >= 80 || narrow) {
      const s3 = narrow ? 8 : Math.max(9, h * 0.13);
      ctx.fillStyle = '#d1d5db';
      fit(ctx, `${n} pages`, '600', s3, pw - 10, 8);
      ctx.fillText(`${n} ${n === 1 ? 'page' : 'pages'}`, 8, y);
    }

    // right: every page, one after the other, scrolling past (the top of each page, with its number)
    if (!n) return;
    const th = h - 14, tw = th * (narrow ? 1.05 : 1.25), gap = narrow ? 5 : 8;
    const unit = tw + gap;
    const off = (t * 34) % (n * unit);
    ctx.save();
    ctx.beginPath();
    ctx.rect(pw, 0, w - pw, h);
    ctx.clip();
    const first = Math.floor(off / unit);
    for (let k = first - 1; k < first + Math.ceil((w - pw) / unit) + 2; k++) {
      const x = pw + 4 + k * unit - off;
      if (x > w || x + tw < pw) continue;
      const idx = ((k % n) + n) % n;
      const im = pages.current[idx];
      rounded(ctx, x, 7, tw, th, 4);
      ctx.save();
      ctx.clip();
      ctx.fillStyle = '#374151';
      ctx.fillRect(x, 7, tw, th);
      if (im && im.complete && im.naturalWidth) {
        const sw = im.naturalWidth;
        const sh = Math.min(im.naturalHeight, sw * (th / tw));
        ctx.drawImage(im, 0, 0, sw, sh, x, 7, tw, th);
      }
      ctx.restore();
      const br = Math.max(7, h * 0.1);
      ctx.beginPath();
      ctx.arc(x + tw - br - 3, 7 + th - br - 3, br, 0, Math.PI * 2);
      ctx.fillStyle = '#dc2626';
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `800 ${Math.max(8, br * 1.05)}px ${FONT}`;
      ctx.fillText(String(idx + 1), x + tw - br - 3, 7 + th - br - 2.5);
    }
    ctx.restore();
    // soft fade where the pages come out from behind the logo panel
    const fg = ctx.createLinearGradient(pw, 0, pw + 18, 0);
    fg.addColorStop(0, '#111827');
    fg.addColorStop(1, 'rgba(17,24,39,0)');
    ctx.fillStyle = fg;
    ctx.fillRect(pw, 0, 18, h);
  });
  return <canvas ref={ref} className="lb-video" role="img" aria-label={`Newspaper for ${label}: all ${n} pages`} />;
}
